import intl from "react-intl-universal";

export const BAAS_CARD_ACCOUNT_STATUSES = ["PENDING", "ACTIVE", "BLOCKED", "CLOSED"];

export const EMPTY_BAAS_CARD_ACCOUNTS_FILTERS = {
  searchQuery: "",
  statuses: [],
  programIds: [],
  productCodes: [],
  linkedStates: [],
};

export const getBaasCardAccountStatusOptions = () =>
  BAAS_CARD_ACCOUNT_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.cartoes.accounts.status.${status}`),
  }));

export const getBaasCardAccountLinkedOptions = () => [
  {
    id: "linked",
    label: intl.get("baas.cartoes.accounts.filters.linkedYes"),
  },
  {
    id: "unlinked",
    label: intl.get("baas.cartoes.accounts.filters.linkedNo"),
  },
];

export const getBaasCardAccountProgramOptions = (accounts = []) => {
  const seen = new Map();

  accounts.forEach((account) => {
    const id = account?.cardProgramId;
    if (!id || seen.has(id)) return;
    seen.set(id, account.cardProgramName || id);
  });

  return Array.from(seen.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getBaasCardAccountProductOptions = (accounts = []) => {
  const seen = new Set();

  accounts.forEach((account) => {
    const code = account?.psProductCode || account?.product?.code;
    if (!code || seen.has(code)) return;
    seen.add(code);
  });

  return Array.from(seen)
    .sort((a, b) => a.localeCompare(b))
    .map((code) => ({ id: code, label: code }));
};

const matchesSearch = (account, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [
    account.personName,
    account.personId,
    account.id,
    account.issuerAccountId,
    account.accountId,
    account.cardProgramName,
    account.cardProgramId,
    account.psProductCode,
    account.product?.code,
    account.product?.name,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesLinked = (account, linkedStates) => {
  if (!linkedStates?.length) return true;
  const isLinked = Boolean(account.accountId);
  if (isLinked && linkedStates.includes("linked")) return true;
  if (!isLinked && linkedStates.includes("unlinked")) return true;
  return false;
};

export const filterBaasCardAccounts = (
  items = [],
  filters = EMPTY_BAAS_CARD_ACCOUNTS_FILTERS,
) =>
  items.filter((account) => {
    if (!matchesSearch(account, filters.searchQuery)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(account.status)) {
      return false;
    }
    if (filters.programIds?.length && !filters.programIds.includes(account.cardProgramId)) {
      return false;
    }
    if (filters.productCodes?.length) {
      const code = account.psProductCode || account.product?.code;
      if (!filters.productCodes.includes(code)) return false;
    }
    if (!matchesLinked(account, filters.linkedStates)) return false;
    return true;
  });

export const hasActiveBaasCardAccountsFilters = (
  filters = EMPTY_BAAS_CARD_ACCOUNTS_FILTERS,
) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.statuses?.length ||
      filters.programIds?.length ||
      filters.productCodes?.length ||
      filters.linkedStates?.length,
  );

export const isBaasCardAccountsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "status":
      return Boolean(filters.statuses?.length);
    case "program":
      return Boolean(filters.programIds?.length);
    case "product":
      return Boolean(filters.productCodes?.length);
    case "linked":
      return Boolean(filters.linkedStates?.length);
    default:
      return false;
  }
};

export const buildBaasCardAccountsFilterTags = (
  filters = EMPTY_BAAS_CARD_ACCOUNTS_FILTERS,
  accounts = [],
) => {
  const tags = [];
  const statusOptions = getBaasCardAccountStatusOptions();
  const programOptions = getBaasCardAccountProgramOptions(accounts);
  const productOptions = getBaasCardAccountProductOptions(accounts);
  const linkedOptions = getBaasCardAccountLinkedOptions();

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((statusId) => statusOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  if (filters.programIds?.length) {
    tags.push({
      key: "programIds",
      iconType: "payment",
      value: filters.programIds
        .map((programId) => programOptions.find((item) => item.id === programId)?.label ?? programId)
        .join(", "),
    });
  }

  if (filters.productCodes?.length) {
    tags.push({
      key: "productCodes",
      iconType: "trace",
      value: filters.productCodes
        .map((code) => productOptions.find((item) => item.id === code)?.label ?? code)
        .join(", "),
    });
  }

  if (filters.linkedStates?.length) {
    tags.push({
      key: "linkedStates",
      iconType: "subAccount",
      value: filters.linkedStates
        .map((state) => linkedOptions.find((item) => item.id === state)?.label ?? state)
        .join(", "),
    });
  }

  return tags;
};

export const removeBaasCardAccountsFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "statuses":
      return { ...filters, statuses: [] };
    case "programIds":
      return { ...filters, programIds: [] };
    case "productCodes":
      return { ...filters, productCodes: [] };
    case "linkedStates":
      return { ...filters, linkedStates: [] };
    default:
      return filters;
  }
};
